import type {
  DailyMetrics,
  HistoryRow,
  ProductData,
  ProductRecord,
  ProductSegmentAssignment,
  ProductSegments,
  ProductSegmentTag,
} from './types'
import { rowToMetrics, sumMetrics } from './utils'

export interface ProductSegmentIndex {
  tagsById: Map<string, ProductSegmentTag>
  skuIdsBySpu: Map<string, string[]>
  assignmentsBySkuTag: Map<string, Map<string, ProductSegmentAssignment[]>>
  assignmentsByProductTag: Map<string, Map<string, ProductSegmentAssignment[]>>
}

export interface ProductBadge {
  id: string
  label: string
  tone: string
  title: string
}

const productKey = (level: string, id: string) => `${level}:${id}`

const appendAssignment = (
  target: Map<string, Map<string, ProductSegmentAssignment[]>>,
  key: string,
  assignment: ProductSegmentAssignment,
) => {
  const byTag = target.get(key) || new Map<string, ProductSegmentAssignment[]>()
  const assignments = byTag.get(assignment.tagId) || []
  assignments.push(assignment)
  byTag.set(assignment.tagId, assignments)
  target.set(key, byTag)
}

export function buildProductSegmentIndex(data: ProductData | null, segments: ProductSegments | null): ProductSegmentIndex {
  const tagsById = new Map((segments?.tags || []).map((tag) => [tag.id, tag]))
  const skuIdsBySpu = new Map<string, string[]>()
  const assignmentsBySkuTag = new Map<string, Map<string, ProductSegmentAssignment[]>>()
  const assignmentsByProductTag = new Map<string, Map<string, ProductSegmentAssignment[]>>()

  for (const product of data?.products || []) {
    if (product.level !== 'sku' || !product.spuId) continue
    const ids = skuIdsBySpu.get(product.spuId) || []
    ids.push(product.id)
    skuIdsBySpu.set(product.spuId, ids)
  }

  for (const assignment of segments?.assignments || []) {
    for (const id of assignment.productIds) {
      appendAssignment(assignmentsByProductTag, productKey(assignment.productLevel, id), assignment)
      if (assignment.productLevel === 'sku') {
        appendAssignment(assignmentsBySkuTag, id, assignment)
      } else if (assignment.includeChildren) {
        for (const skuId of skuIdsBySpu.get(id) || []) {
          appendAssignment(assignmentsBySkuTag, skuId, assignment)
        }
      }
    }
  }

  return { tagsById, skuIdsBySpu, assignmentsBySkuTag, assignmentsByProductTag }
}

export const assignmentActiveOn = (assignment: ProductSegmentAssignment, date: string) =>
  (!assignment.startDate || date >= assignment.startDate)
  && (!assignment.endDate || date <= assignment.endDate)

export const assignmentOverlapsDates = (assignment: ProductSegmentAssignment, dates: string[]) =>
  dates.some((date) => assignmentActiveOn(assignment, date))

export const skuTagAssignments = (index: ProductSegmentIndex, skuId: string, tagId: string) =>
  index.assignmentsBySkuTag.get(skuId)?.get(tagId) || []

export const skuMatchesTag = (index: ProductSegmentIndex, skuId: string, tagId: string, dates: string[]) =>
  !tagId || skuTagAssignments(index, skuId, tagId).some((assignment) => assignmentOverlapsDates(assignment, dates))

export const activeDatesForSkuTag = (
  index: ProductSegmentIndex,
  skuId: string,
  tagId: string,
  dates: string[],
) => {
  if (!tagId) return dates
  const assignments = skuTagAssignments(index, skuId, tagId)
  return dates.filter((date) => assignments.some((assignment) => assignmentActiveOn(assignment, date)))
}

const uniqueAssignments = (assignments: ProductSegmentAssignment[]) =>
  Array.from(new Map(assignments.map((assignment) => [assignment.id, assignment])).values())

export function assignmentsForProductTag(
  index: ProductSegmentIndex,
  product: ProductRecord,
  tagId: string,
): ProductSegmentAssignment[] {
  const direct = index.assignmentsByProductTag.get(productKey(product.level, product.id))?.get(tagId) || []
  if (product.level === 'sku') {
    return uniqueAssignments([...direct, ...skuTagAssignments(index, product.id, tagId)])
  }
  const childAssignments = (index.skuIdsBySpu.get(product.id) || [])
    .flatMap((skuId) => skuTagAssignments(index, skuId, tagId))
  return uniqueAssignments([...direct, ...childAssignments])
}

export function badgesForProduct(
  index: ProductSegmentIndex,
  product: ProductRecord,
  date: string,
): ProductBadge[] {
  const childSkuIds = product.level === 'spu' ? index.skuIdsBySpu.get(product.id) || [] : []
  return Array.from(index.tagsById.values()).flatMap((tag) => {
    const assignments = assignmentsForProductTag(index, product, tag.id)
      .filter((assignment) => assignmentActiveOn(assignment, date))
    if (!assignments.length) return []

    let label = tag.name
    if (product.level === 'spu' && childSkuIds.length) {
      const matchedCount = childSkuIds.filter((skuId) =>
        skuTagAssignments(index, skuId, tag.id).some((assignment) => assignmentActiveOn(assignment, date)),
      ).length
      if (matchedCount > 0 && matchedCount < childSkuIds.length) label = `${tag.name} ${matchedCount}/${childSkuIds.length} SKU`
    }
    const details = assignments
      .map((assignment) => assignment.activityName || assignment.note || assignment.source)
      .filter(Boolean)
      .join('；')
    return [{ id: tag.id, label, tone: tag.tone, title: details || tag.description }]
  })
}

const metricsToRow = (metrics: DailyMetrics): HistoryRow => [
  metrics.date,
  metrics.gmv,
  metrics.soldItemCount,
  metrics.orderCount,
  metrics.customerCount,
  metrics.conversionRate,
  metrics.averageOrderValue,
  metrics.averageItemPrice,
  metrics.productVisitors,
  metrics.productPageviews,
  metrics.searchImpressions,
  metrics.searchClicks,
  metrics.cartItemCount,
  metrics.cancelRefundAmount,
  metrics.cancelRefundItemCount,
]

export function productWithAllowedDates(product: ProductRecord, allowedDates: Set<string>): ProductRecord {
  return {
    ...product,
    history: product.history.filter((row) => allowedDates.has(row[0])),
  }
}

export function aggregateSkuProducts(
  product: ProductRecord,
  skus: ProductRecord[],
  allowedDatesBySku?: Map<string, Set<string>>,
): ProductRecord {
  const rowsByDate = new Map<string, DailyMetrics[]>()
  for (const sku of skus) {
    const allowedDates = allowedDatesBySku?.get(sku.id)
    for (const row of sku.history) {
      if (allowedDates && !allowedDates.has(row[0])) continue
      const rows = rowsByDate.get(row[0]) || []
      rows.push(rowToMetrics(row))
      rowsByDate.set(row[0], rows)
    }
  }
  const history = Array.from(rowsByDate.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, rows]) => metricsToRow(sumMetrics(rows, date)))
  return { ...product, history }
}
